import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';

import { BrandHeader } from '@/components/layout/BrandHeader';
import { useQuestions, useCases, usePokemon } from '@/hooks/useConfig';
import { useGoogleAuth } from '@/features/auth/useGoogleAuth';
import { useStore, useUser } from '@/app/store';
import '@/styles/welcome.css';

const AUTH_ERR: Record<string, string> = {
  'auth/popup-closed-by-user': 'Cerraste la ventana de Google antes de completar.',
  'auth/popup-blocked':         'El navegador bloqueó el popup. Permite popups y reintenta.',
  'auth/cancelled-popup-request': 'Ya hay un login en curso.',
  'auth/operation-not-allowed': 'Google Sign-In no está habilitado en Firebase Console.',
  'auth/unauthorized-domain':   'Este dominio no está autorizado en Firebase.',
  'auth/network-request-failed': 'Sin conexión. Revisa tu red.',
};

export function WelcomePage() {
  const navigate = useNavigate();
  const user = useUser();
  const { signIn } = useGoogleAuth();
  const showToast = useStore((s) => s.showToast);
  const [authBusy, setAuthBusy] = useState(false);

  const { list: questions } = useQuestions();
  const { list: cases } = useCases();
  const { list: pokemon } = usePokemon();

  const totalItems = questions.length + cases.length + pokemon.length;

  /** Asegura sesión Google y resuelve a true si está autenticado. */
  const ensureAuth = async (): Promise<boolean> => {
    if (user) return true;
    setAuthBusy(true);
    try {
      await signIn();
      return true;
    } catch (err) {
      const code = err instanceof FirebaseError ? err.code : 'unknown';
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        showToast(AUTH_ERR[code] ?? `Error: ${code}`, 'error');
      }
      return false;
    } finally {
      setAuthBusy(false);
    }
  };

  const handlePlay = async () => {
    const ok = await ensureAuth();
    if (!ok) return;
    const player = useStore.getState().currentPlayer;
    navigate(player ? '/menu' : '/register');
  };

  const handleRanking = () => {
    navigate('/leaderboard');
  };

  return (
    <div className="welcome-root">
      <div className="bg-grid" />
      <div className="bg-glow glow-a" />
      <div className="bg-glow glow-b" />
      <div className="bg-glow glow-c" />

      <div className="wrap">
        <header>
          <BrandHeader />
          <div className="status">
            <span className="pulse" />
            EVENTO ACTIVO
          </div>
        </header>

        <section className="hero">
          <div className="presenta">eTribe presenta</div>

          <h1 className="title">
            <span className="l1">DATA</span>
            <span className="l2">CHALLENGE</span>
          </h1>

          <div className="year">2026</div>

          <p className="lede">
            3 juegos sobre <strong>Datos, Cloud e Inteligencia Artificial</strong>.
          </p>
          <p className="lede dim">Compite por el ranking individual y por equipos.</p>

          <div className="cta-row">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePlay}
              disabled={authBusy}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
              </svg>
              {authBusy ? 'CONECTANDO...' : 'JUGAR AHORA'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleRanking}
              disabled={authBusy}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
              VER RANKING
            </button>
          </div>

          <div className="discover">
            <span className="chev">▼</span>
            DESCUBRE LOS DESAFÍOS
            <span className="chev">▼</span>
          </div>
        </section>

        <section className="stats">
          <div className="stat">
            <div className="stat-label">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                <path d="M4 22h16" />
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
              </svg>
              JUEGOS
            </div>
            <div className="stat-num">3</div>
          </div>
          <div className="stat">
            <div className="stat-label">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              ITEMS
            </div>
            <div className="stat-num">{totalItems}</div>
          </div>
          <div className="stat">
            <div className="stat-label">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9 12 2" />
              </svg>
              MAX PTS
            </div>
            <div className="stat-num">20.000</div>
          </div>
        </section>

        <div className="sec-head">
          <div className="sec-title">
            <span className="bar" />
            LOS 3 DESAFÍOS
          </div>
          <div className="sec-line" />
          <div className="sec-tag">LISTOS</div>
        </div>

        <section className="cards">
          {/* 01 — SPEED DATA */}
          <article className="card amber">
            <div className="card-top">
              <div className="card-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <div className="card-idx">01</div>
            </div>
            <h3 className="card-name">SPEED DATA</h3>
            <p className="card-desc">5 preguntas · 30s c/u</p>
            <div className="card-foot">
              <span className="card-max">
                MAX<b>9.000</b>
              </span>
              <span className="card-items">{questions.length} items</span>
            </div>
          </article>

          {/* 02 — POKÉMON / BIGDATA */}
          <article className="card green">
            <div className="card-top">
              <div className="card-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="6" y1="11" x2="10" y2="11" />
                  <line x1="8" y1="9" x2="8" y2="13" />
                  <line x1="15" y1="12" x2="15.01" y2="12" />
                  <line x1="18" y1="10" x2="18.01" y2="10" />
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                </svg>
              </div>
              <div className="card-idx">02</div>
            </div>
            <h3 className="card-name">POKÉMON / BIGDATA</h3>
            <p className="card-desc">5 rondas · 10s c/u</p>
            <div className="card-foot">
              <span className="card-max">
                MAX<b>5.000</b>
              </span>
              <span className="card-items">{pokemon.length} items</span>
            </div>
          </article>

          {/* 03 — CASO DESCIFRADO */}
          <article className="card red">
            <div className="card-top">
              <div className="card-ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <div className="card-idx">03</div>
            </div>
            <h3 className="card-name">CASO DESCIFRADO</h3>
            <p className="card-desc">5 pistas progresivas</p>
            <div className="card-foot">
              <span className="card-max">
                MAX<b>6.000</b>
              </span>
              <span className="card-items">{cases.length} items</span>
            </div>
          </article>
        </section>
      </div>
    </div>
  );
}
